<script setup>
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, onMounted, ref, defineOptions } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

defineOptions({ name: 'EmailAntispamSettings' });

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const records = computed(() => getters.getEmailAntispamEntries.value);
const uiFlags = computed(() => getters.getEmailAntispamUIFlags.value);

const newEmail = ref('');
const emailError = ref('');
const loading = ref({});

const tableHeaders = computed(() => [
  t('EMAIL_ANTISPAM.LIST.TABLE_HEADER.EMAIL'),
  t('EMAIL_ANTISPAM.LIST.TABLE_HEADER.ACTIONS'),
]);

onMounted(() => {
  store.dispatch('getEmailAntispamEntries');
});

const validateEntry = value => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const domainRegex = /^@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
  return emailRegex.test(value) || domainRegex.test(value);
};

const addEntry = async () => {
  emailError.value = '';
  const trimmed = newEmail.value.trim();
  if (!trimmed) {
    emailError.value = t('EMAIL_ANTISPAM.FORM.EMAIL.ERROR_REQUIRED');
    return;
  }
  if (!validateEntry(trimmed)) {
    emailError.value = t('EMAIL_ANTISPAM.FORM.EMAIL.ERROR_INVALID');
    return;
  }
  try {
    await store.dispatch('createEmailAntispamEntry', trimmed);
    useAlert(t('EMAIL_ANTISPAM.ADD.API.SUCCESS_MESSAGE'));
    newEmail.value = '';
  } catch (error) {
    useAlert(error?.message || t('EMAIL_ANTISPAM.ADD.API.ERROR_MESSAGE'));
  }
};

const removeEntry = async entry => {
  loading.value[entry.id] = true;
  try {
    await store.dispatch('deleteEmailAntispamEntry', entry.id);
    useAlert(t('EMAIL_ANTISPAM.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('EMAIL_ANTISPAM.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[entry.id] = false;
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('EMAIL_ANTISPAM.LOADING')"
    :no-records-found="false"
    no-records-message=""
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('EMAIL_ANTISPAM.HEADER')"
        :description="$t('EMAIL_ANTISPAM.DESCRIPTION')"
        feature-name="email_antispam"
      >
        <template #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('EMAIL_ANTISPAM.COUNT', { n: records.length }) }}
          </span>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #preBody>
      <div class="flex flex-col gap-2 mb-6">
        <label class="text-body-main font-medium text-n-slate-12">
          {{ $t('EMAIL_ANTISPAM.FORM.EMAIL.LABEL') }}
        </label>
        <div class="flex gap-2 items-start">
          <div class="flex flex-col gap-1 flex-1">
            <input
              v-model="newEmail"
              type="text"
              class="w-full px-3 py-2 border border-n-slate-6 rounded-lg text-body-main text-n-slate-12 bg-n-alpha-1 focus:outline-none focus:ring-2 focus:ring-n-brand-9"
              :placeholder="$t('EMAIL_ANTISPAM.FORM.EMAIL.PLACEHOLDER')"
              @keydown.enter="addEntry"
            />
            <span v-if="emailError" class="text-body-sm text-n-ruby-11">
              {{ emailError }}
            </span>
          </div>
          <Button
            :label="$t('EMAIL_ANTISPAM.ADD.BUTTON_TEXT')"
            size="sm"
            :is-loading="uiFlags.creatingItem"
            @click="addEntry"
          />
        </div>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-6">
        <BaseTable
          v-if="records.length"
          :headers="tableHeaders"
          :items="records"
        >
          <template #row="{ items }">
            <BaseTableRow v-for="entry in items" :key="entry.id" :item="entry">
              <template #default>
                <BaseTableCell class="max-w-0">
                  <span class="text-body-main text-n-slate-12 truncate block">
                    {{ entry.email }}
                  </span>
                </BaseTableCell>
                <BaseTableCell align="end" class="w-24">
                  <Button
                    v-tooltip.top="$t('EMAIL_ANTISPAM.DELETE.BUTTON_TEXT')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[entry.id]"
                    @click="removeEntry(entry)"
                  />
                </BaseTableCell>
              </template>
            </BaseTableRow>
          </template>
        </BaseTable>

        <p v-else class="text-body-main text-n-slate-11">
          {{ $t('EMAIL_ANTISPAM.LIST.EMPTY') }}
        </p>
      </div>
    </template>
  </SettingsLayout>
</template>
